import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import {
  PoCheckboxGroupOption,
  PoDialogService,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoTableColumn,
} from "@po-ui/ng-components";
import { Observable, Subscription, concat, of } from "rxjs";
import { last, tap } from "rxjs/operators";

import { MainService } from "src/app/app.service";
import { RoleTag } from "src/app/shared/role-tags";
import { Area } from "src/app/interfaces/area";
import { Tribe } from "src/app/interfaces/tribe";
import { AccessAuditEntry, AccessAuditService } from "../services/access-audit.service";
import { AccessCatalogEntry, AccessCatalogService } from "../services/access-catalog.service";
import { AreaComboFilter } from "../services/area-combo.filter";
import { UserTransferService } from "../services/user-transfer.service";
import { UserAccessItem, UsersAccessService } from "../services/users-access.service";
import { describeAudit, diffManagedRoles, groupCatalog, auditActionTag, auditActorLabel, auditRoleTags, formatAuditWhen } from "../users-admin.utils";

interface AccessGroupView {
  group: string;
  label: string;
  options: Array<PoCheckboxGroupOption>;
  selected: Array<string>;
}

/**
 * Edição de um usuário num modal único: lotação (Área; a tribe vem dela e é só leitura)
 * e acessos em colunas por produto, com os botões sempre visíveis. O histórico é um
 * modal à parte, aberto pela ação "Histórico" da lista.
 */
@Component({
  selector: "app-user-edit",
  templateUrl: "./user-edit.component.html",
  styles: [
    `
      .user-edit-area-tribe {
        margin-left: 8px;
        color: var(--color-neutral-mid-60, #6e7c7f);
        font-size: 0.875rem;
      }
      .audit-roles {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
      }
    `,
  ],
})
export class UserEditComponent implements OnDestroy {
  @Input() tribes: Array<Tribe> = [];
  @Input() areas: Array<Area> = [];
  @Input() catalog: Array<AccessCatalogEntry> = [];
  @Output() saved = new EventEmitter<void>();

  @ViewChild("editModal", { static: true }) editModal: PoModalComponent;
  @ViewChild("historyModal", { static: true }) historyModal: PoModalComponent;

  savePrimary: PoModalAction = { label: "Salvar", action: () => this.save(), disabled: true };
  readonly saveCancel: PoModalAction = { label: "Cancelar", action: () => this.close() };

  user: UserAccessItem | null = null;
  areaId = "";
  roles: Array<string> = [];
  saving = false;

  readonly areaCombo = new AreaComboFilter();
  groups: Array<AccessGroupView> = [];

  history: Array<{ when: string; action: RoleTag; roles: Array<RoleTag & { key: string }>; details: string; actor: string }> = [];
  loadingHistory = false;

  readonly historyColumns: Array<PoTableColumn> = [
    { property: "when", label: "Quando", width: "180px" },
    { property: "action", label: "O que mudou", type: "cellTemplate", width: "180px" },
    { property: "details", label: "Detalhes", type: "cellTemplate" },
    { property: "actor", label: "Alterado por", width: "190px" },
  ];

  private subscriptions = new Subscription();

  constructor(
    private usersAccessService: UsersAccessService,
    private userTransferService: UserTransferService,
    private accessCatalogService: AccessCatalogService,
    private auditService: AccessAuditService,
    private mainService: MainService,
    private poDialog: PoDialogService,
    private poNotification: PoNotificationService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  open(user: UserAccessItem): void {
    this.user = user;
    this.areaId = user.areaId ?? "";
    this.roles = [...(user.roles || [])];
    this.areaCombo.setData(this.areas, this.tribes);
    this.refreshGroups();
    this.refreshPrimary();
    this.editModal.open();
  }

  openHistory(user: UserAccessItem): void {
    this.user = user;
    this.loadHistory(user.username);
    this.historyModal.open();
  }

  close(): void {
    this.editModal.close();
  }

  private refreshPrimary(): void {
    const n = this.changesCount;
    this.savePrimary = { ...this.savePrimary, label: n ? `Salvar (${n})` : "Salvar", disabled: !n || this.saving, loading: this.saving };
  }

  get subtitle(): string {
    return this.user?.updatedAt ? `Atualizado em ${new Date(this.user.updatedAt).toLocaleDateString("pt-BR")}` : "";
  }

  // ── Lotação ─────────────────────────────────────────────────────────────────

  onAreaChange(areaId: string): void {
    this.areaId = areaId ?? "";
    this.refreshPrimary();
  }

  get selectedArea(): Area | undefined {
    return this.areas.find((a) => a.id === this.areaId);
  }

  get tribeName(): string {
    const tribe = this.selectedArea?.tribe;
    return (tribe && this.tribes.find((t) => t.id === tribe)?.tribe) || "";
  }

  // ── Acessos ─────────────────────────────────────────────────────────────────

  private refreshGroups(): void {
    this.groups = groupCatalog(this.catalog).map((g) => ({
      group: g.group,
      label: g.label,
      options: g.entries.map((e) => ({ value: e.key, label: e.label })),
      selected: this.roles.filter((r) => g.entries.some((e) => e.key === r)),
    }));
  }

  trackGroup = (_: number, g: AccessGroupView) => g.group;

  /** Marca/desmarca acessos de um grupo; dependências (chat requer mcp) acompanham. */
  onGroupRolesChange(group: string, selected: Array<string>): void {
    const groupKeys = this.catalog.filter((e) => e.group === group).map((e) => e.key);
    const next = [...this.roles.filter((r) => !groupKeys.includes(r)), ...(selected ?? [])];
    this.roles = this.accessCatalogService.applyDependencies(this.catalog, next, this.roles);
    this.refreshGroups();
    this.refreshPrimary();
  }

  // ── Salvar ──────────────────────────────────────────────────────────────────

  get roleChanges(): { add: Array<string>; remove: Array<string> } {
    return diffManagedRoles(this.user?.roles ?? [], this.roles, this.catalog);
  }

  get areaChanged(): boolean {
    return !!this.user && (this.user.areaId ?? "") !== this.areaId;
  }

  get changesCount(): number {
    const { add, remove } = this.roleChanges;
    return add.length + remove.length + (this.areaChanged ? 1 : 0);
  }

  save(): void {
    if (!this.user || !this.changesCount) {
      return;
    }
    const { add, remove } = this.roleChanges;
    const isSelf = this.user.username.toLowerCase() === (this.mainService.userToken.username || "").toLowerCase();
    if (remove.includes("superuser") && isSelf) {
      this.poNotification.warning("Você não pode remover o seu próprio acesso superuser.");
      return;
    }

    const lines: Array<string> = [];
    if (this.areaChanged) {
      const from = this.user.areaName || "sem área";
      const to = this.selectedArea?.area || "sem área";
      lines.push(`Área: ${from} → <strong>${to}</strong>`);
    }
    if (add.length) lines.push(`Concede: <strong>${add.join(", ")}</strong>`);
    if (remove.length) lines.push(`Revoga: <strong>${remove.join(", ")}</strong>`);

    this.poDialog.confirm({
      title: `Salvar ${this.user.username}`,
      message: lines.join("<br/>"),
      confirm: () => this.persist(),
    });
  }

  private persist(): void {
    if (!this.user) {
      return;
    }
    const username = this.user.username;
    const { add, remove } = this.roleChanges;
    const steps: Array<Observable<unknown>> = [];
    let areaSaved = false;

    if (this.areaChanged) {
      const transfer = this.areaId ? this.userTransferService.transfer(this.areaId, [username]) : this.userTransferService.unlinkArea(username);
      steps.push(transfer.pipe(tap(() => (areaSaved = true))));
    }
    if (add.length || remove.length) {
      steps.push(this.usersAccessService.bulkUpdateRoles({ usernames: [username], add, remove }));
    }

    this.saving = true;
    this.refreshPrimary();
    this.subscriptions.add(
      concat(...steps, of(null))
        .pipe(last())
        .subscribe({
          next: () => {
            this.poNotification.success(`Alterações de "${username}" salvas.`);
            this.saved.emit();
            this.close();
          },
          complete: () => this.endSaving(),
          error: () => {
            // A área já foi gravada e os acessos não: a lista reflete a nova área e o modal
            // fica aberto só com os acessos pendentes para tentar de novo.
            if (areaSaved && this.user) {
              this.user = { ...this.user, areaId: this.areaId || undefined, areaName: this.selectedArea?.area, tribeName: this.tribeName || undefined };
              this.saved.emit();
            }
            this.endSaving();
          },
        }),
    );
  }

  private endSaving(): void {
    this.saving = false;
    this.refreshPrimary();
  }

  // ── Histórico ───────────────────────────────────────────────────────────────

  private loadHistory(username: string): void {
    this.history = [];
    this.loadingHistory = true;
    this.subscriptions.add(
      this.auditService.list({ target: username, targetType: "user", limit: 10 }).subscribe({
        next: (page) =>
          (this.history = page.items.map((e: AccessAuditEntry) => ({
            when: formatAuditWhen(e.createdAt),
            action: auditActionTag(e.action, e.details),
            roles: auditRoleTags(e),
            details: describeAudit(e),
            actor: auditActorLabel(e.actor),
          }))),
        complete: () => (this.loadingHistory = false),
        error: () => (this.loadingHistory = false),
      }),
    );
  }
}
